import React, { useState, useEffect } from "react";
import {
    NavLink,
    useHistory,
    useLocation,
    useParams
} from "react-router-dom";
import {
    Group,
    Store,
    Add,
    AddBusiness,
    LanguageOutlined,
    Campaign,
    AdsClick,
    LocalOffer,
    DynamicFeed,
    Help,
    // Edit,
    RateReview,
    ContactPhone,
    Map,
    ListAlt,
    Block
} from "@mui/icons-material";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import MarkEmailReadRoundedIcon from "@mui/icons-material/MarkEmailReadRounded";
import NotificationsActiveRoundedIcon from "@mui/icons-material/NotificationsActiveRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";

import styled from "styled-components";

import { THEME_MAP } from "../theme";

import { useUserRoles } from "../globalstore";
import {
    FormControl,
    Select,
    MenuItem,
    ListSubheader,
    TextField,
    InputAdornment,
    IconButton,
    Tooltip
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useBarUtils, useSelectedBar } from "../actions/bars";
import { functions } from "../services/firebase";
import { captureError } from "../lib/errorReporting";
import { useBarOwnerTour } from "../components/onboarding/barOwnerTour";
import { useIsMobile } from "../hooks/useIsMobile";
import { useSidebar } from "../contexts/SidebarContext";
import { APP_VERSION } from "../lib/appVersion";

export function Navigation() {
    const { userRoles: { isAdmin, roles } } = useUserRoles();
    // `isAdmin` may arrive as a real boolean (Firestore emulator / native
    // boolean field) or the string "true" (legacy data). Normalize so the
    // Admin Menu shows in every case.
    const admin = isAdmin === true || isAdmin === "true";
    return (
        <Nav>
            <img
                alt="Bar Hopper"
                src="/logo2.png"
                height="100px"
                width="100px"
            />
            <BarOwnersNav roles={roles} isAdmin={isAdmin} />
            {admin && (
                    <>
                        <h3>Admin Menu</h3>
                        <NavLink to="/bars">
                            <Store />
                            Bars
                        </NavLink>
                        <NavLink to="/bar/new" exact>
                            <AddBusiness />
                            Bar Creation
                        </NavLink>
                        <NavLink to="/regions">
                            <LanguageOutlined />
                            Regions
                        </NavLink>
                        <NavLink to="/users">
                            <Group />
                            Users
                        </NavLink>
                        <NavLink to="/promotions" exact>
                            <Campaign />
                            Promotions
                        </NavLink>
                        <NavLink to="/local-ads" exact>
                            <AdsClick />
                            Local Advertisements
                        </NavLink>
                        <NavLink to="/feed-ads" exact>
                            <DynamicFeed />
                            Feed Advertisements
                        </NavLink>
                        <NavLink to="/admin/category-definitions" exact>
                            <ListAlt />
                            Category Definitions
                        </NavLink>
                        <NavLink to="/admin/contacted-bars" exact>
                            <ContactPhone />
                            Contacted Bars
                        </NavLink>
                        <NavLink to="/admin/claimed-bar-review" exact>
                            <RateReview />
                            Claimed Bar Review
                        </NavLink>
                        <NavLink to="/admin/email-system" exact>
                            <MarkEmailReadRoundedIcon />
                            Email System
                        </NavLink>
                        <NavLink to="/admin/notification-system" exact>
                            <NotificationsActiveRoundedIcon />
                            Notification System
                        </NavLink>
                        {/* <NavLink to="/how-to-editor" exact>
                            <Edit />
                            How To Editor
                        </NavLink> */}
                        <h3>Development Tools</h3>
                        <NavLink to="/admin/system-config" exact>
                            <TuneRoundedIcon />
                            System Config
                        </NavLink>
                        <NavLink to="/admin/osm-data" exact>
                            <Map />
                            OSM Bar Discovery
                        </NavLink>
                        <NavLink to="/admin/nblb" exact>
                            <Map />
                            No Bar Left Behind
                        </NavLink>
                        <NavLink to="/admin/statewide-search" exact>
                            <Map />
                            Statewide Search
                        </NavLink>
                        <NavLink to="/admin/blacklist" exact>
                            <Block />
                            Excluded Venues
                        </NavLink>
                    </>
                )}
            <VersionTag>Barhopper Web App: v{APP_VERSION}</VersionTag>
        </Nav>
    );
}

const Nav = styled.nav`
    width: 300px;
    min-height: 100vh;

    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;

    background: ${THEME_MAP["color-primary-500"]};

    a {
        display: flex;
        align-items: center;

        text-decoration: none;
        padding: 8px 24px;
        width: 100%;

        color: ${THEME_MAP["color-primary-200"]};

        &.active {
            color: ${THEME_MAP["color-basic-100"]};
            background: linear-gradient(
                to right,
                ${THEME_MAP["color-primary-400"]},
                ${THEME_MAP["color-primary-400"]} 75%,
                ${THEME_MAP["color-primary-500"]} 100%
            );
            box-shadow: -35px 1px 6px rgba(0, 0, 0, 0.1);
        }

        &:hover {
            color: ${THEME_MAP["color-basic-100"]};
            text-decoration: none;
        }

        svg {
            margin-right: 8px;
            width: 1.6rem;
            height: 1.6rem;
        }
    }

    img {
        width: 100px;
        height: auto;

        margin: 16px auto 32px;
    }

    h3 {
        width: 100%;
        color: ${THEME_MAP["color-basic-100"]};
        margin-top: 32px;
        padding-left: 16px;
    }

    @media (max-width: 767px) {
        width: 100%;
        padding-bottom: 32px;
    }
`;

// Pinned to the bottom of the sidebar via margin-top: auto.
const VersionTag = styled.div`
    margin-top: auto;
    width: 100%;
    padding: 16px;
    text-align: center;
    font-size: 0.8rem;
    color: ${THEME_MAP["color-primary-200"]};
`;

const barOwnerPathReg = /(bar|managers|promotions|premium\/placements)\/[\w\d]{20}$/; // this should match urls that are for barOwners

function BarOwnersNav({ roles, isAdmin }) {
    const history = useHistory();
    const location = useLocation();
    const { id: pathId } = useParams();
    const { subscription, paymentProviderId } = useUserRoles();
    const { setSelectedBar, searchBarsByName } = useBarUtils();
    const selectedBar = useSelectedBar();

    const admin = isAdmin === true || isAdmin === "true";

    const [bars, setBars] = useState([]);
    const [barSearch, setBarSearch] = useState("");
    const [adminResults, setAdminResults] = useState([]);
    //const [promotions, setPromotions] = useState([]);
    const [subscriptionUrl, setSubscriptionUrl] = useState("");
    const hasRoles = bars.length > 0;

    // On mobile the nav lives in an off-canvas drawer that stays mounted, so the
    // tour should only auto-start once the sidebar is actually open. On desktop
    // the nav is always visible, so the sidebar state doesn't gate it.
    const isMobile = useIsMobile();
    const { isOpen: isSidebarOpen } = useSidebar();
    const { startTour } = useBarOwnerTour(
        hasRoles && Boolean(selectedBar?.id) && (!isMobile || isSidebarOpen)
    );

    useEffect(() => {
        if (roles && Object.keys(roles).length > 0) {
            const tempBars = Object.entries(roles).map(
                ([barId, barRole]) => {
                    return { id: barId, ...barRole };
                }
            );
            setBars(tempBars);
            // Only seed the default selection once. Re-asserting an existing
            // selection re-fetches it into the shared viewed-bar state, which
            // would clobber whatever bar the user is actively editing (e.g. a
            // bar opened directly via /managers/:id) whenever roles refresh.
            if (!selectedBar && tempBars[0]) {
                setSelectedBar(tempBars[0]);
            }
            if (paymentProviderId) {
                functions
                    .httpsCallable("visitMyCustomerPortal")({
                        paymentProviderId,
                        currentLocation: window.location.href
                    })
                    .then((data) => {
                        const url = data.data;
                        setSubscriptionUrl(url);
                    })
                    .catch((err) => {
                        captureError(err, { component: "Navigation", action: "visitMyCustomerPortal" });
                        console.error("Failed to fetch customer portal URL", err);
                    });
            }
        } else {
            setBars([]);
            setSelectedBar(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [roles, paymentProviderId]);

    // Admins can edit any bar, not just the ones they manage. When an admin
    // types a search term, query all bars by name so the selector can surface
    // bars outside their own roles map.
    useEffect(() => {
        const query = barSearch.trim();
        if (!admin || !query) {
            setAdminResults([]);
            return undefined;
        }
        let active = true;
        const handle = setTimeout(async () => {
            const results = await searchBarsByName(query);
            if (active) setAdminResults(results);
        }, 250);
        return () => {
            active = false;
            clearTimeout(handle);
        };
    }, [barSearch, admin, searchBarsByName]);

    const handleBarsSelect = (e) => {
        const barId = e.target.value;
        if (barId === "noop") return;
        const roleBar = bars.find(b => b.id === barId);
        const bar = roleBar || adminResults.find(b => b.id === barId);
        if (bar) setSelectedBar(bar);
        if (!roleBar) {
            // Admin picked a bar they don't manage — open its edit page directly.
            if (barId) history.push(`/bar/${barId}`);
            return;
        }
        let tempPath = location.pathname;
        if (tempPath.match(barOwnerPathReg)) {
            tempPath = tempPath.replace(pathId, barId);
            history.push(tempPath);
        }
    };

    const sortedBars = React.useMemo(
        () => [...bars].sort((a, b) =>
            (a.barName || "").localeCompare(b.barName || "", undefined, { sensitivity: "base" })
        ),
        [bars]
    );

    const filteredBars = React.useMemo(() => {
        const query = barSearch.trim().toLowerCase();
        if (!query) return sortedBars;
        return sortedBars.filter((bar) =>
            (bar.barName || "").toLowerCase().includes(query)
        );
    }, [sortedBars, barSearch]);

    // For admins with an active search, merge their role bars with the
    // all-bars search results (deduped) so they can jump to any bar.
    const displayBars = React.useMemo(() => {
        if (admin && barSearch.trim()) {
            // Plain object dedupe — the MUI `Map` icon import shadows global Map.
            const byId = {};
            filteredBars.forEach((bar) => { byId[bar.id] = bar; });
            adminResults.forEach((bar) => { byId[bar.id] = bar; });
            return Object.values(byId).sort((a, b) =>
                (a.barName || "").localeCompare(b.barName || "", undefined, { sensitivity: "base" })
            );
        }
        return filteredBars;
    }, [admin, barSearch, adminResults, filteredBars]);

    // Keep the currently selected bar present as an option so MUI's Select has a
    // matching item even when it isn't in the current (role/search) list.
    const menuBars = React.useMemo(() => {
        if (selectedBar?.id && !displayBars.some((bar) => bar.id === selectedBar.id)) {
            return [...displayBars, { id: selectedBar.id, barName: selectedBar.barName }];
        }
        return displayBars;
    }, [displayBars, selectedBar]);

    if (!hasRoles) {
        // Admins manage bars through the Admin Menu (Bars / Bar Creation) and
        // never need to claim one, so don't show the claim-bar prompt to them.
        if (isAdmin === true || isAdmin === "true") {
            return null;
        }
        return (
            <BarOwnerNavContainer>
                <NavLink to="/claim-bar">
                    <AddBusiness />
                    Claim Your Bar
                </NavLink>
            </BarOwnerNavContainer>
        );
    }

    return (
        <BarOwnerNavContainer>
            <FormControl className={"manage-bar-select"} data-tour="bar-select">
                <Select
                    value={selectedBar?.id || ""}
                    renderValue={(value) => {
                        // Find the bar name based on the selected ID (value)
                        const currentBar = menuBars.find(b => b.id === value);
                        return currentBar?.barName || selectedBar?.barName || "Select Bar"; // Display name or placeholder
                    }}
                    onChange={handleBarsSelect}
                    onClose={() => setBarSearch("")}
                    displayEmpty
                    inputProps={{ "aria-label": "Current Bar" }}
                    color="primary"
                    className="selectWidth"
                    MenuProps={{
                        autoFocus: false,
                        PaperProps: { style: { maxHeight: 420 } }
                    }}
                >
                    <ListSubheader
                        sx={{
                            position: "sticky",
                            top: 0,
                            zIndex: 2,
                            backgroundColor: "background.paper",
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            padding: "8px 12px",
                            lineHeight: 1
                        }}
                        // Stop key events so MUI Select doesn't treat the
                        // subheader as a focusable item during typeahead.
                        onKeyDown={(e) => e.stopPropagation()}
                    >
                        <TextField
                            size="small"
                            autoFocus
                            fullWidth
                            placeholder="Search bars..."
                            value={barSearch}
                            onChange={(e) => setBarSearch(e.target.value)}
                            onKeyDown={(e) => {
                                // Prevent MUI Select's built-in typeahead from
                                // stealing keystrokes while typing in the search box.
                                if (e.key !== "Escape") e.stopPropagation();
                            }}
                            onClick={(e) => e.stopPropagation()}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" />
                                    </InputAdornment>
                                )
                            }}
                        />
                        {(isAdmin === true || isAdmin === "true") && (
                            <Tooltip title="Create Bar">
                                <IconButton
                                    aria-label="Create Bar"
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        history.push("/bar/new");
                                    }}
                                    sx={{
                                        flexShrink: 0,
                                        border: `2px solid ${THEME_MAP["color-primary-500"]}`,
                                        color: THEME_MAP["color-primary-500"],
                                        "&:hover": {
                                            backgroundColor: THEME_MAP["color-primary-500"],
                                            color: "#fff"
                                        }
                                    }}
                                >
                                    <Add fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                    </ListSubheader>
                    {displayBars.length === 0 && (
                        <MenuItem disabled value="noop">
                            No bars match
                        </MenuItem>
                    )}
                    {menuBars.map((bar) => (
                        <MenuItem key={bar.id} value={bar.id}>
                            {bar.barName}
                        </MenuItem>
                    ))}
                    {/* {promotions.map((promotion) => (
                        <MenuItem key={promotion.id} value={promotion}>
                            {promotion.promotionName}
                        </MenuItem>
                    ))} */}
                </Select>
            </FormControl>

            <NavLink to={`/bar/${selectedBar?.id}`} data-tour="profile">
                <Store />
                Profile
            </NavLink>
            <NavLink to={`/managers/${selectedBar?.id}`} data-tour="managers">
                <Group />
                Managers
            </NavLink>
            <NavLink to={`/specials/${selectedBar?.id}`} data-tour="specials">
                <LocalOffer />
                Specials
            </NavLink>
            <NavLink to={`/promotions/${selectedBar?.id}`} data-tour="promotions">
                <Campaign />
                Promotions
            </NavLink>
            
            {/* Add Take a tour link for all users */}
            <a
                href="#take-a-tour"
                onClick={(event) => {
                    event.preventDefault();
                    startTour();
                }}
            >
                <Help />
                Take a tour
            </a>

            {/* <NavLink to="/how-to" exact>
                <Help />
                How To
            </NavLink> */}
            
            <h3
                style={{
                    fontSize: "16px",
                    marginBottom: "10px",
                    textTransform: "uppercase"
                }}
            >
                Premium Services
            </h3>
            <NavLink to={`/premium/placements/${selectedBar?.id}`} data-tour="placements">
                <img
                    src="/premium-placement.png"
                    style={{
                        width: "23px",
                        height: "auto",
                        margin: "0px",
                        marginRight: "10px"
                    }}
                    alt="Premium Placements"
                />
                Featured placements
            </NavLink>
            {subscription && (
                <a href={subscriptionUrl}>
                    <AccountBalanceIcon />
                    Billing
                </a>
            )}
        </BarOwnerNavContainer>
    );
}

const BarOwnerNavContainer = styled.section`
    width: 100%;

    .manage-bar-select {
        width: 100%;
        padding: 0 16px;

        color: ${THEME_MAP["color-basic-100"]};

        /* These are all overrides of the Material-UI defaults */
        .MuiInputBase-input {
            color: ${THEME_MAP["color-basic-100"]};
        }
        .MuiInput-underline:before {
            border-color: transparent;
        }
        .MuiInput-underline:hover:not(.Mui-disabled):before {
            border-color: transparent;
        }
        .MuiSelect-icon {
            color: ${THEME_MAP["color-basic-100"]};
        }
        .MuiInput-underline:after {
            border-color: ${THEME_MAP["color-basic-100"]};
        }
        .MuiSelect-select:focus {
            background: none;
        }
    }
    .selectWidth {
          width: 270px;
    }

    @media (max-width: 767px) {
        .selectWidth {
            width: 100%;
        }
    }
`;
